import { Download, Loader2, Maximize2, Minus, Plus, RotateCcw, RotateCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadCrossOrigin } from '../lib/download-blob';

interface Props {
  src: string;
  alt?: string;
  filename?: string;
  onClose: () => void;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 8;

/**
 * Lightbox de imagem inline — backdrop preto + viewport completo.
 *
 * Features:
 *  - Fechar: tecla Esc, click no backdrop, botão X
 *  - Zoom: scroll do mouse, botões + / - / 1:1 / fit
 *  - Pan: drag (quando zoom > 1)
 *  - Download: link direto (a tag com `download` attr)
 *  - Click + double-click no canvas reseta zoom
 *  - Bloqueia scroll da página de fundo
 */
export function ImageLightbox({ src, alt, filename, onClose }: Props) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; offX: number; offY: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  /** Bytes da imagem em tamanho cheio ainda vindo. É a espera mais longa do
   *  painel (arquivo original, sem redimensionar) e antes o lightbox abria
   *  preto até a foto surgir. Aqui NÃO há o piscar da bolha: o lightbox só
   *  monta quando o usuário clica, então cada abertura é um carregamento de
   *  verdade — do cache do navegador ou da rede. */
  const [carregando, setCarregando] = useState(true);
  /** Giro só depois de 100ms — foto já vista abre instantânea, sem piscar.
   *  Mesma regra da bolha (vide MediaContent). */
  const [mostrarGiro, setMostrarGiro] = useState(false);
  useEffect(() => {
    if (!carregando) return undefined;
    const id = setTimeout(() => setMostrarGiro(true), 100);
    return () => clearTimeout(id);
  }, [carregando]);

  const reset = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  const rotate = useCallback(() => {
    setRotation((r) => (r + 90) % 360);
  }, []);

  const zoomBy = useCallback((delta: number, cx?: number, cy?: number) => {
    setZoom((z) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta));
      // Se o zoom mudou e tem ponto focal, mantém o ponto sob o cursor
      if (next !== z && cx !== undefined && cy !== undefined && imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        const cxRel = cx - (rect.left + rect.width / 2);
        const cyRel = cy - (rect.top + rect.height / 2);
        const ratio = next / z;
        setOffset((o) => ({ x: o.x - cxRel * (ratio - 1), y: o.y - cyRel * (ratio - 1) }));
      }
      return next;
    });
  }, []);

  // Esc fecha; bloqueia scroll do body enquanto aberto
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === '+' || e.key === '=') zoomBy(ZOOM_STEP);
      else if (e.key === '-' || e.key === '_') zoomBy(-ZOOM_STEP);
      else if (e.key === '0') reset();
      else if (e.key === 'r' || e.key === 'R') rotate();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, zoomBy, reset, rotate]);

  const handleWheel = (e: React.WheelEvent): void => {
    e.preventDefault();
    const delta = -Math.sign(e.deltaY) * ZOOM_STEP;
    zoomBy(delta, e.clientX, e.clientY);
  };

  const startDrag = (e: React.MouseEvent): void => {
    if (zoom <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, offX: offset.x, offY: offset.y };
  };

  const onDragMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging || !dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setOffset({ x: dragStartRef.current.offX + dx, y: dragStartRef.current.offY + dy });
    },
    [dragging],
  );

  const endDrag = useCallback(() => {
    setDragging(false);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [dragging, onDragMove, endDrag]);

  const handleDoubleClick = (): void => {
    if (zoom === 1) {
      setZoom(2);
    } else {
      reset();
    }
  };

  const handleImageLoad = (): void => {
    setCarregando(false);
    if (imgRef.current) {
      setNaturalSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  const handleDownload = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await downloadCrossOrigin(src, filename ?? 'imagem');
    } catch (err) {
      console.error('download failed', err);
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click é UX padrão de lightbox
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt ?? 'Imagem ampliada'}
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Botão fechar no canto superior direito */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-pill bg-black/60 text-white hover:bg-black/80"
        aria-label="Fechar"
        title="Fechar (Esc)"
      >
        <X size={18} />
      </button>

      {/* Canvas centralizado — click na borda transparente fecha */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: canvas é interativo via mouse/wheel */}
      <div
        className="relative flex h-full w-full select-none items-center justify-center overflow-hidden"
        onWheel={handleWheel}
        onMouseDown={startDrag}
        onDoubleClick={handleDoubleClick}
        onClick={(e) => {
          // Fecha apenas se o click foi na área vazia (não na imagem ou toolbar)
          if (e.target === e.currentTarget) onClose();
        }}
        style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-out' }}
      >
        {carregando && mostrarGiro && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <Loader2 size={36} className="animate-spin text-white/80" />
          </div>
        )}
        <img
          ref={imgRef}
          src={src}
          alt={alt ?? ''}
          onLoad={handleImageLoad}
          onError={() => setCarregando(false)}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          className={`rounded-md bg-n-0 object-contain shadow-2xl transition-transform duration-75 ${
            carregando ? 'opacity-0' : 'opacity-100'
          }`}
          style={{
            maxHeight: 'min(70vh, 700px)',
            maxWidth: 'min(70vw, 900px)',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
          }}
        />
      </div>

      {/* Toolbar inferior central — pill com ícones */}
      <div
        className="-translate-x-1/2 absolute bottom-6 left-1/2 flex items-center gap-1 rounded-pill bg-black/70 px-2 py-1.5 text-white shadow-lg backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => zoomBy(-ZOOM_STEP)}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Reduzir zoom"
          title="Reduzir (−)"
        >
          <Minus size={16} />
        </button>
        <span className="min-w-[44px] text-center font-mono text-[11px] tabular-nums text-white/80">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => zoomBy(ZOOM_STEP)}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Ampliar zoom"
          title="Ampliar (+)"
        >
          <Plus size={16} />
        </button>
        <span className="mx-1 h-5 w-px bg-white/20" />
        <button
          type="button"
          onClick={() => setZoom(1)}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Tamanho 1:1"
          title="Tamanho original"
        >
          <Maximize2 size={16} />
        </button>
        <button
          type="button"
          onClick={reset}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Resetar zoom"
          title="Resetar (0)"
        >
          <RotateCcw size={16} />
        </button>
        <span className="mx-1 h-5 w-px bg-white/20" />
        <button
          type="button"
          onClick={rotate}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Girar 90 graus"
          title="Girar 90° (R)"
        >
          <RotateCw size={16} />
        </button>
        <span className="mx-1 h-5 w-px bg-white/20" />
        <button
          type="button"
          onClick={(e) => void handleDownload(e)}
          className="flex h-8 w-8 items-center justify-center rounded-pill hover:bg-white/15"
          aria-label="Baixar imagem"
          title="Baixar"
        >
          <Download size={16} />
        </button>
        {naturalSize && (
          <>
            <span className="mx-1 h-5 w-px bg-white/20" />
            <span className="px-2 font-mono text-[10px] text-white/50">
              {naturalSize.w}×{naturalSize.h}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
